import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import ArticleIcon from '@mui/icons-material/Article';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { 
  FaDownload, 
  FaPlay, 
  FaFileAlt 
} from 'react-icons/fa';
import { 
  HiOutlineDownload, 
  HiOutlinePlay, 
  HiOutlineDocument 
} from 'react-icons/hi';
import marketingTheme from '../themes/marketingTheme';
import { 
  RiDownloadLine, 
  RiPlayLine, 
  RiFileTextLine 
} from 'react-icons/ri';

// Import metadata
import resourcesMetadata from '../insights/metadata.js';
import { getArticleUrl } from '../utils/slugUtils';

const ResourceCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#2D2D35',
  border: '1px solid #404047',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
  },
}));

const FilterSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#2D2D35',
  padding: '24px',
  borderRadius: '8px',
  marginBottom: '32px',
  border: '1px solid #404047',
}));

const ResourceLibrary = () => {
  const [filters, setFilters] = useState({
    downloadable: false,
    blogPosts: false,
    media: false,
  });

  const [resources, setResources] = useState([]);

  useEffect(() => {
    // Transform metadata into resources format
    const transformedResources = resourcesMetadata.map((item, index) => ({
      id: index + 1,
      title: item.title,
      description: item.description,
      type: item.type,
      thumbnail: item.thumbnail_path,
      category: item.category,
      url: getArticleUrl(item),
      author: item.author_name,
      createdAt: item.created_at,
      tags: item.tags
    }));
    setResources(transformedResources);
  }, []);

  const handleFilterChange = (event) => {
    setFilters({
      ...filters,
      [event.target.name]: event.target.checked,
    });
  };

  // Sample resource data - replace with actual data from API
  // const resources = [
  //   {
  //     id: 1,
  //     title: 'Industrial AI Implementation Guide',
  //     description: 'A comprehensive guide to implementing AI solutions in industrial environments.',
  //     type: 'downloadable',
  //     thumbnail: '/images/data_driven_industrial_automation.png',
  //     category: 'Guide',
  //     url: '/resources/ai-implementation-guide.pdf',
  //   },
  //   {
  //     id: 2,
  //     title: 'Data to Wisdom: Part 1',
  //     description: 'Understanding the journey from raw data to actionable wisdom in industrial contexts.',
  //     type: 'blogPosts',
  //     thumbnail: '/images/data_information_knowledge_wisdom.png',
  //     category: 'Blog Post',
  //     url: '/blog/data-to-wisdom-part1',
  //   },
  //   {
  //     id: 3,
  //     title: 'Reducing Risk in Machine Learning',
  //     description: 'Best practices for minimizing risks when deploying ML solutions in production.',
  //     type: 'blogPosts',
  //     thumbnail: '/images/machine_learning.jpg',
  //     category: 'Blog Post',
  //     url: '/blog/reduce-risk-ml',
  //   },
  //   {
  //     id: 4,
  //     title: 'MotionLogic Demo Video',
  //     description: 'See how MotionLogic transforms industrial workflows with computer vision.',
  //     type: 'media',
  //     thumbnail: '/images/process_segmentation.png',
  //     category: 'Video',
  //     url: 'https://www.youtube.com/watch?v=example',
  //   },
  //   // {
  //   //   id: 5,
  //   //   title: 'Capability Statement',
  //   //   description: 'Overview of Traversal Labs capabilities and service offerings.',
  //   //   type: 'downloadable',
  //   //   thumbnail: '/images/capability_statement_preview.png',
  //   //   category: 'Document',
  //   //   url: '/resources/capability-statement.pdf',
  //   // },
  //   // {
  //   //   id: 6,
  //   //   title: 'How We Operate',
  //   //   description: 'Learn about our approach to delivering industrial AI solutions.',
  //   //   type: 'blogPosts',
  //   //   thumbnail: '/images/manufacturing_data_example.jpg',
  //   //   category: 'Blog Post',
  //   //   url: '/blog/how-we-operate',
  //   // },
  // ];

  const filteredResources = resources.filter((resource) => {
    if (!filters.downloadable && !filters.blogPosts && !filters.media) {
      return true; // Show all if no filters selected
    }
    return filters[resource.type];
  });

  const getResourceIcon = (type) => {
    switch (type) {
      case 'downloadable':
        return <HiOutlineDownload />;
      case 'media':
        return <RiPlayLine />;
      default:
        return <FaFileAlt />;
    }
  };

  const getResourceButtonText = (type) => {
    switch (type) {
      case 'downloadable':
        return 'Download';
      case 'media':
        return 'Watch';
      default:
        return 'Read';
    }
  };

  return (
    <Box sx={{ pt: 10, pb: 8, backgroundColor: '#24242A', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '32px', md: '48px' },
              fontWeight: 700,
              color: '#FFFFFF',
              mb: 2,
            }}
          >
            Resource Library
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: '18px',
              fontWeight: 400,
              color: '#D1D5DB',
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Resources to help you set and reach your automation goals.
          </Typography>
        </Box>

        {/* Filters */}
        <FilterSection>
          <Typography
            variant="h6"
            sx={{ mb: 3, color: '#FFFFFF', textAlign: 'center', fontWeight: 600 }}
          >
            Filter Resources
          </Typography>
          <FormGroup
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'center',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 2, sm: 4 },
              px: { xs: 2, sm: 0 },
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.downloadable}
                  onChange={handleFilterChange}
                  name="downloadable"
                  sx={{
                    color: '#9CA3AF',
                    '&.Mui-checked': {
                      color: marketingTheme.palette.primary.main,
                    },
                  }}
                />
              }
              label="Downloadable Resources"
              sx={{
                '& .MuiFormControlLabel-label': {
                  color: '#E5E7EB',
                  fontSize: '16px',
                  fontWeight: 500,
                },
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.blogPosts}
                  onChange={handleFilterChange}
                  name="blogPosts"
                  sx={{
                    color: '#9CA3AF',
                    '&.Mui-checked': {
                      color: marketingTheme.palette.primary.main,
                    },
                  }}
                />
              }
              label="Blog Posts"
              sx={{
                '& .MuiFormControlLabel-label': {
                  color: '#E5E7EB',
                  fontSize: '16px',
                  fontWeight: 500,
                },
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.media}
                  onChange={handleFilterChange}
                  name="media"
                  sx={{
                    color: '#9CA3AF',
                    '&.Mui-checked': {
                      color: marketingTheme.palette.primary.main,
                    },
                  }}
                />
              }
              label="Media"
              sx={{
                '& .MuiFormControlLabel-label': {
                  color: '#E5E7EB',
                  fontSize: '16px',
                  fontWeight: 500,
                },
              }}
            />
          </FormGroup>
        </FilterSection>

        {/* Resources Grid */}
        <Grid container spacing={4}>
          {filteredResources.map((resource) => (
            <Grid item xs={12} sm={6} md={4} key={resource.id}>
              <ResourceCard>
                <CardMedia
                  component="img"
                  height="200"
                  image={resource.thumbnail}
                  alt={resource.title}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={resource.category}
                      size="small"
                      sx={{
                        backgroundColor: marketingTheme.palette.primary.main,
                        color: '#000000',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: '#FFFFFF',
                      mb: 1,
                      fontSize: '18px',
                    }}
                  >
                    {resource.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: '#D1D5DB',
                      mb: 3,
                      flexGrow: 1,
                      fontSize: '14px',
                      lineHeight: 1.5,
                    }}
                  >
                    {resource.description}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={getResourceIcon(resource.type)}
                    fullWidth
                    sx={{
                      backgroundColor: marketingTheme.palette.primary.main,
                      '&:hover': { backgroundColor: marketingTheme.palette.primary.dark },
                      mt: 'auto',
                    }}
                    onClick={() => window.open(resource.url, '_blank')}
                  >
                    {getResourceButtonText(resource.type)}
                  </Button>
                </CardContent>
              </ResourceCard>
            </Grid>
          ))}
        </Grid>

        {filteredResources.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" sx={{ color: '#D1D5DB' }}>
              No resources match your current filters.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ResourceLibrary;
